/**
 * 「孩子点开始打卡时，预计时长该预填多少」—— ✅ **全项目只有这一处**。
 *
 * ## 为什么要有这个模块
 *
 * 孩子端点「开始」会弹窗问「预计要做多久？」（服务端要求 `estimatedSeconds`）。
 * 改动之前默认值是：
 *
 *   1. 这条打卡**上一次**的预计（`estimatedSeconds`）；
 *   2. 没有就 **20 分钟**。
 *
 * 但家长建任务时其实**已经设过目标**（`targetValue` + `unit`，例如「每天阅读 30 分钟」），
 * 孩子每次却要从 20 分钟手动改到 30 —— 家长的目标在这条路径上完全没用上。
 *
 * 现在改成三段优先级：
 *
 *   | 优先级 | 来源 | 例子 |
 *   | --- | --- | --- |
 *   | 1 | **家长设的目标**（单位是时间时） | 目标 30 分钟 → 预填 30 |
 *   | 2 | 上次这条打卡的预计 | 上次填 25 分钟 → 预填 25 |
 *   | 3 | 兜底 20 分钟 | 什么都没设 → 20 |
 *
 * ## 为什么"上次的预计"排在家长目标之后
 *
 * 家长设的是**硬目标**（"每天阅读 30 分钟"），上次孩子自己填的只是**那一次的心情**。
 * 若把上次排前面，孩子第一次填 10 分钟之后，之后每天都会默认 10 分钟，
 * 家长的目标就永久失效了 —— 而这正是这次要修的问题。
 *
 * ## 单位怎么算
 *
 * 家长的任务可以是「30 分钟」「1 小时」「90 秒」这类时间单位，也可以是「3 遍」「2 页」
 * 这种与时长无关的单位。**只有能换算成分钟的才用**，其余一律跳过（落到优先级 2/3）：
 *
 *   | 单位写法 | 系数 |
 *   | --- | --- |
 *   | 分钟 / 分 / min / mins / minute / minutes / m | ×1 |
 *   | 小时 / 时 / h / hr / hrs / hour / hours | ×60 |
 *   | 秒 / s / sec / secs / second / seconds | ÷60（向上取整到分钟，最少 1） |
 *
 * 结果一律夹到 `[1, 720]`：与服务端 `estimatedSeconds` 的上限（12 小时）一致，
 * 免得家长把目标写成 1000 分钟、孩子点开始直接被服务端拒绝。
 *
 * 纯函数：无 React / DOM / 环境依赖，前后端都能引。
 */

/** 什么都没设时的兜底分钟数（与改动前一致，别悄悄改）。 */
export const START_ESTIMATE_FALLBACK_MINUTES = 20;
/** 服务端允许的最短预计（分钟）。 */
export const START_ESTIMATE_MIN_MINUTES = 1;
/** 服务端允许的最长预计（分钟）= 12 小时。 */
export const START_ESTIMATE_MAX_MINUTES = 720;

/** 预填值的来源，界面上据此决定要不要写「家长设的目标：X 分钟」。 */
export type StartEstimateSource = 'parent-target' | 'last-time' | 'fallback';

export interface StartEstimate {
  /** 预填到输入框里的分钟数（已夹到 [1, 720]）。 */
  minutes: number;
  source: StartEstimateSource;
  /** 家长目标的原始分钟数（`null` = 没设或换算不了）；仅 `parent-target` 时有值。 */
  targetMinutes: number | null;
}

/** 单位 → 换算成分钟的系数；`null` = 这个单位跟时长无关（遍/页/题…）。 */
function minutesFactorOf(unit: string | null | undefined): number | null {
  const text = String(unit ?? '').trim().toLowerCase();
  if (!text) return null;
  if (['分钟', '分', 'min', 'mins', 'minute', 'minutes', 'm'].includes(text)) return 1;
  if (['小时', '时', 'h', 'hr', 'hrs', 'hour', 'hours'].includes(text)) return 60;
  if (['秒', 's', 'sec', 'secs', 'second', 'seconds'].includes(text)) return 1 / 60;
  return null;
}

/** 把分钟数夹到服务端允许的范围（非有限值 → 兜底）。 */
function clampMinutes(value: number): number {
  if (!Number.isFinite(value)) return START_ESTIMATE_FALLBACK_MINUTES;
  return Math.min(START_ESTIMATE_MAX_MINUTES, Math.max(START_ESTIMATE_MIN_MINUTES, Math.round(value)));
}

/**
 * 家长设的目标 → 分钟数；不是时间单位或值非法时返回 `null`。
 */
export function targetMinutesOf(
  targetValue: number | null | undefined,
  unit: string | null | undefined,
): number | null {
  if (typeof targetValue !== 'number' || !Number.isFinite(targetValue) || targetValue <= 0) return null;
  const factor = minutesFactorOf(unit);
  if (factor === null) return null;
  const minutes = targetValue * factor;
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  return clampMinutes(minutes);
}

/**
 * 计算「开始打卡」弹窗的预填值。
 *
 * @param input.targetValue 家长设的目标量（`null` = 没设量化目标）
 * @param input.unit 目标单位（`分钟` / `遍` / …）
 * @param input.estimatedSeconds 这条打卡上一次填的预计（秒）
 */
export function defaultStartEstimate(input: {
  targetValue?: number | null;
  unit?: string | null;
  estimatedSeconds?: number | null;
}): StartEstimate {
  const targetMinutes = targetMinutesOf(input.targetValue, input.unit);
  if (targetMinutes !== null) {
    return { minutes: targetMinutes, source: 'parent-target', targetMinutes };
  }

  const last = input.estimatedSeconds;
  if (typeof last === 'number' && Number.isFinite(last) && last > 0) {
    return { minutes: clampMinutes(last / 60), source: 'last-time', targetMinutes: null };
  }

  return { minutes: START_ESTIMATE_FALLBACK_MINUTES, source: 'fallback', targetMinutes: null };
}

/**
 * 弹窗里的快选按钮：常用值 + 家长目标（若它不在常用值里）。
 *
 * 为什么要把目标塞进去：家长目标可能是 25 / 40 / 50 分钟，
 * 不在 `[10,15,20,30,45,60]` 里；不加这颗按钮，孩子点错一次就再也回不到目标值。
 * 目标值排在最前面，并且升序合并（同一分钟只出现一次）。
 */
export function startEstimatePresets(
  targetMinutes: number | null,
  base: readonly number[] = [10, 15, 20, 30, 45, 60],
): number[] {
  const all = new Set<number>();
  if (typeof targetMinutes === 'number' && Number.isFinite(targetMinutes) && targetMinutes > 0) {
    all.add(clampMinutes(targetMinutes));
  }
  for (const value of base) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      all.add(clampMinutes(value));
    }
  }
  return [...all].sort((a, b) => a - b);
}
